import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Avatar,
  Chip,
  Typography,
  TextField,
  InputAdornment,
  Box,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Button,
  CircularProgress,
} from "@material-ui/core";
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Sync as SyncIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
} from "@material-ui/icons";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import toastUtils from "../../utils/toastUtils";
import ConfirmationModal from "../ConfirmationModal";
import TableRowSkeleton from "../TableRowSkeleton";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  searchContainer: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: "center",
    flexWrap: "wrap",
  },
  searchField: {
    minWidth: 300,
  },
  filterField: {
    minWidth: 150,
  },
  productImage: {
    width: 50,
    height: 50,
    borderRadius: 8,
  },
  productName: {
    fontWeight: 500,
    marginBottom: 4,
  },
  productDescription: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    maxWidth: 200,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  priceText: {
    fontWeight: 600,
    color: theme.palette.success.main,
  },
  stockText: {
    fontWeight: 500,
  },
  lowStock: {
    color: theme.palette.warning.main,
  },
  outOfStock: {
    color: theme.palette.error.main,
  },
  syncStatus: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  actionButton: {
    padding: 4,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
}));

const ProductList = ({ onEdit, onRefresh, refreshTrigger }) => {
  const classes = useStyles();
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [syncFilter, setSyncFilter] = useState("");
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    loadProducts();
    loadCategories();
  }, [page, rowsPerPage, searchTerm, categoryFilter, statusFilter, syncFilter, refreshTrigger]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm,
        category: categoryFilter,
        status: statusFilter,
        syncStatus: syncFilter,
      };

      const { data } = await api.get("/catalog/products", { params });
      setProducts(data.products);
      setTotalCount(data.totalCount);
    } catch (err) {
      console.error("Error loading products:", err);
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const { data } = await api.get("/catalog/categories");
      setCategories(data.categories);
    } catch (err) {
      console.error("Error loading categories:", err);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleCategoryFilterChange = (event) => {
    setCategoryFilter(event.target.value);
    setPage(0);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleSyncFilterChange = (event) => {
    setSyncFilter(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleToggleActive = async (product) => {
    try {
      await api.put(`/catalog/products/${product.id}`, {
        ...product,
        isActive: !product.isActive,
      });
      toastUtils.success(`Produto ${product.isActive ? 'desativado' : 'ativado'} com sucesso!`);
      loadProducts();
    } catch (err) {
      console.error("Error toggling product status:", err);
      toastError(err);
    }
  };

  const handleToggleFeatured = async (product) => {
    try {
      await api.put(`/catalog/products/${product.id}`, {
        ...product,
        isFeatured: !product.isFeatured,
      });
      toastUtils.success(`Produto ${product.isFeatured ? 'removido dos' : 'adicionado aos'} destaques!`);
      loadProducts();
    } catch (err) {
      console.error("Error toggling featured status:", err);
      toastError(err);
    }
  };

  const handleDeleteClick = (product) => {
    setSelectedProduct(product);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/catalog/products/${selectedProduct.id}`);
      toastUtils.success("Produto excluído com sucesso!");
      setDeleteModalOpen(false);
      setSelectedProduct(null);
      loadProducts();
      onRefresh();
    } catch (err) {
      console.error("Error deleting product:", err);
      toastError(err);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price / 100);
  };

  const getSyncStatusIcon = (syncStatus, syncError) => {
    switch (syncStatus) {
      case 'synced':
        return <CheckCircleIcon style={{ color: '#4caf50' }} />;
      case 'pending':
        return <ScheduleIcon style={{ color: '#ff9800' }} />;
      case 'error':
        return (
          <Tooltip title={syncError || 'Erro na sincronização'}>
            <ErrorIcon style={{ color: '#f44336' }} />
          </Tooltip>
        );
      default:
        return <ScheduleIcon style={{ color: '#9e9e9e' }} />;
    }
  };

  const getStockDisplay = (stockQuantity) => {
    if (stockQuantity === 0) {
      return (
        <Typography className={`${classes.stockText} ${classes.outOfStock}`}>
          Sem estoque
        </Typography>
      );
    } else if (stockQuantity <= 5) {
      return (
        <Typography className={`${classes.stockText} ${classes.lowStock}`}>
          {stockQuantity} unidades
        </Typography>
      );
    } else {
      return (
        <Typography className={classes.stockText}>
          {stockQuantity} unidades
        </Typography>
      );
    }
  };

  return (
    <div className={classes.root}>
      {/* Search and Filters */}
      <Box className={classes.searchContainer}>
        <TextField
          className={classes.searchField}
          placeholder="Buscar produtos..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        
        <FormControl className={classes.filterField}>
          <InputLabel>Categoria</InputLabel>
          <Select
            value={categoryFilter}
            onChange={handleCategoryFilterChange}
          >
            <MenuItem value="">Todas</MenuItem>
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.name}>
                {category.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl className={classes.filterField}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={handleStatusFilterChange}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="active">Ativo</MenuItem>
            <MenuItem value="inactive">Inativo</MenuItem>
          </Select>
        </FormControl>

        <FormControl className={classes.filterField}>
          <InputLabel>Sincronização</InputLabel>
          <Select
            value={syncFilter}
            onChange={handleSyncFilterChange}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="synced">Sincronizado</MenuItem>
            <MenuItem value="pending">Pendente</MenuItem>
            <MenuItem value="error">Erro</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Products Table */}
      <Paper className={classes.paper}>
        <TableContainer>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>Produto</TableCell>
                <TableCell>Categoria</TableCell>
                <TableCell>Preço</TableCell>
                <TableCell>Estoque</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Sincronização</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from(new Array(rowsPerPage)).map((_, index) => (
                  <TableRowSkeleton key={index} columns={7} />
                ))
              ) : products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <div className={classes.emptyState}>
                      <Typography variant="h6">
                        Nenhum produto encontrado
                      </Typography>
                      <Typography variant="body2">
                        Adicione produtos ao seu catálogo para começar
                      </Typography>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                products.map((product) => (
                  <TableRow key={product.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar
                          src={product.imageUrl}
                          className={classes.productImage}
                          variant="rounded"
                        >
                          {product.name.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography className={classes.productName}>
                            {product.name}
                          </Typography>
                          <Typography className={classes.productDescription}>
                            {product.description}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={product.category || 'Sem categoria'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography className={classes.priceText}>
                        {formatPrice(product.price)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getStockDisplay(product.stockQuantity)}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          label={product.isActive ? 'Ativo' : 'Inativo'}
                          color={product.isActive ? 'primary' : 'default'}
                          size="small"
                        />
                        {product.isFeatured && (
                          <StarIcon style={{ color: '#ffc107' }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box className={classes.syncStatus}>
                        {getSyncStatusIcon(product.syncStatus, product.syncError)}
                        <Typography variant="caption">
                          {product.syncStatus === 'synced' && 'Sincronizado'}
                          {product.syncStatus === 'pending' && 'Pendente'}
                          {product.syncStatus === 'error' && 'Erro'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar produto">
                        <IconButton
                          className={classes.actionButton}
                          onClick={() => onEdit(product)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title={product.isActive ? 'Desativar' : 'Ativar'}>
                        <IconButton
                          className={classes.actionButton}
                          onClick={() => handleToggleActive(product)}
                        >
                          {product.isActive ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title={product.isFeatured ? 'Remover destaque' : 'Destacar'}>
                        <IconButton
                          className={classes.actionButton}
                          onClick={() => handleToggleFeatured(product)}
                        >
                          {product.isFeatured ? <StarIcon /> : <StarBorderIcon />}
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Excluir produto">
                        <IconButton
                          className={classes.actionButton}
                          onClick={() => handleDeleteClick(product)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
          }
        />
      </Paper>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        title="Excluir Produto"
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
      >
        Tem certeza que deseja excluir o produto "{selectedProduct?.name}"?
        Esta ação não pode ser desfeita.
      </ConfirmationModal>
    </div>
  );
};

export default ProductList;